import { v4 } from "uuid";
import { MeasureTimemapEntry, Note as note } from "./main";
import { dpAssert } from "./helpers";

// trigger event for playwright tests
export function triggerEvent(e: string, data?: any) {
  const event = new CustomEvent(e, { detail: data });
  document.dispatchEvent(event);
}
export function getFirstNoteWithMidiTimeout(notes: note[]): note | undefined {
  let currentNoteWithMidiTimeout = notes[0];
  for (let curr of notes) {
    if (curr.timeNoteOnMidi) {
      currentNoteWithMidiTimeout = curr;
      return currentNoteWithMidiTimeout
    }
  }
  return currentNoteWithMidiTimeout
}
// Function to check if element is inside the selection coordinates
export function isElementInsideX(
  rect: any,
  xCoordStart: number,
  xCoordEnd: number,
): boolean {
  const withinXBounds = rect.left >= xCoordStart && rect.right <= xCoordEnd;
  return withinXBounds;
}

export function createDamperPedalEvent(eventNum: number) {
  if (eventNum < 64) {
    // off event
    return {
      data: [176, 64, 0],
      message: {
        data: [176, 64, 0]
      }
    }
  } else if (eventNum > 64) {
    // on event
    return {
      uuid: v4(),
      data: [176, 64, 127],
      message: {
        data: [176, 64, 127]
      }
    }
  }
  // on event
  return {
    data: [176, 64, 127],
    message: {
      data: [176, 64, 127]
    }
  }
}

const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export function getNoteName(pitchNumber: number): { octave: string; noteName: string } {
  // this is webassembly implementation, but we got freezes with it in PracticeMode.ts 401 (createNoteEvent)
  // so we decided to keep JS implementation for now
  // const result = (window as any).getNoteName(pitchNumber);
  // return result;

  // JS implementation:
  // Calculate octave number based on MIDI pitch number
  const octave = Math.floor((pitchNumber / 12) - 1); // MIDI pitch range: 0-127 corresponds to octaves -1 to 9

  // Determine the note name (chromatic scale) based on MIDI pitch number
  const noteIndex = pitchNumber % 12;
  const noteName = noteNames[noteIndex];

  return { octave: octave.toString(), noteName };
}

// TODO TO WS
export function createNoteEvent(pitch: number, velocity: number | undefined = undefined) {
  const { octave, noteName } = getNoteName(pitch)
  const noteEvent = {
    note: {
      number: pitch,
      rawAttack: velocity ? velocity : 80,
      name: noteName,
      octave: octave,
      attack: velocity ? velocity : 80,
      duration: 1,
      release: velocity ? velocity : 80
    }
  };
  return noteEvent
}

export function getMidiTimeDifferences(notes: note[]) {
  const timeDifferences = [];

  for (let i = 1; i < notes.length; i++) {
    const note = notes[i];
    const prevNote = notes[i - 1];
    if (note &&
      prevNote &&
      (note.timeNoteOnMidi === 0 || note.timeNoteOnMidi) &&
      (prevNote.timeNoteOnMidi === 0 || prevNote.timeNoteOnMidi)
    ) {
      const timeDifference = note.timeNoteOnMidi - prevNote.timeNoteOnMidi;
      timeDifferences.push(timeDifference);
    }
  }

  return timeDifferences;
}

export function showTimeDifferences(notes: note[]) {
  const timeDifferences = [];


  for (let i = 1; i < notes.length; i++) {
    const timeDifference = (notes[i] as any).midi.time - (notes[i - 1] as any).midi.time;
    timeDifferences.push(timeDifference);
  }

  return timeDifferences;
}

export function getEventType(event: any) {
  if (event.message && event.message.data) {
    if (event.message.data[1] === 64) {
      if (event.message.data[2] >= 1) {
        // damper pedal on
        return EVENT_TYPE.pedal_on;
      } else if (event.message.data[2] <= 0) {
        // damper pedal off
        return EVENT_TYPE.pedal_off
      }
    }
  }
  if (event.type && event.type === "noteon") {
    return EVENT_TYPE.note_on
  }
  if (event.type && event.type === "noteoff") {
    return EVENT_TYPE.note_off
  }
  return undefined
}

export enum EVENT_TYPE {
  pedal_on,
  pedal_off,
  note_on,
  note_off
}

export function translateNoteToRussian(note: any) {
  // Mapping of English note names to Russian
  const noteMapping: any = {
    "C": "До",
    "D": "Ре",
    "E": "Ми",
    "F": "Фа",
    "G": "Соль",
    "A": "Ля",
    "B": "Си"
  };
  // Convert note to uppercase and return the Russian equivalent or "Unknown note"
  return noteMapping[note.toUpperCase()] || "Unknown note";
}

export function getVoltaNumber(measure: MeasureTimemapEntry) {
  let currentMeasureVoltaNumber = -1;
  if (measure.volta && measure.volta.endingStart) {
    currentMeasureVoltaNumber = parseInt(measure.volta.endingStart.endingNumber);
  }
  if (measure.volta && measure.volta.endingStop) {
    currentMeasureVoltaNumber = parseInt(measure.volta.endingStop.endingNumber);
  }
  return currentMeasureVoltaNumber;
}

// copy only that note without it's links to other notes (next and prev links)
export function noteCopySingle(obj: any) {

  if (typeof obj !== 'object' || obj === null) {
    return obj; // Return primitive types and null as is
  }

  const copy: any = Array.isArray(obj) ? [] : {};

  for (let key in obj) {
    // if it's note and it's not a OrnamentNote (it should be with valid id) then skip it
    if (obj[key] && obj[key].hasOwnProperty("noteId") && obj[key]['noteId'].length > 0) {
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      copy[key] = noteCopySingle(obj[key]);
    }
  }

  return copy;
}


export function getCacheFromTimemap(timemap: any): any {
  const cache: any = {};
  let index = 0;
  for (const measure of timemap) {
    for (const note of measure.notes) {
      cache[index] = cacheNote(note);
      index++;
    }
  }
  return cache;
}

function cacheNote(note: note): any {

  dpAssert(note)

  // copy note without deep note links to other notes (prev, next etc...)
  const noteCopy: any = noteCopySingle(note);
  if (note.ornamentNotes) {
    noteCopy.ornamentNotes = note.ornamentNotes;
    dpAssert(noteCopy.ornamentNotes.length === note.ornamentNotes.length, "Tried to cache note, but didn't copy all of it's ornament notes");
  }

  return {
    velocityNoteOn: noteCopy.velocityNoteOn,
    velocityNoteOff: noteCopy.velocityNoteOff,
    ornamentNotes: noteCopy.ornamentNotes,
    timeNoteOffMidi: noteCopy.timeNoteOffMidi,
    timeNoteOnMidi: noteCopy.timeNoteOnMidi
  }
}